import gql from 'graphql-tag';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  allPass,
  anyPass,
  compose,
  filter,
  memoizeWith,
  pick,
  T,
  toLower,
  isEmpty,
} from 'ramda';
import React, {memo, useEffect, useMemo, useState, useCallback} from 'react';
import {Query} from '@apollo/react-components';
import {useLazyQuery} from '@apollo/react-hooks';
import {withRouter} from 'react-router';
import {Text} from '../atoms/Text';
import {withErrorBoundary} from '../molecules/ErrorBoundary';
import ExpandableTableRow from '../molecules/ExpandableTableRow';
import LabelledInfo from '../molecules/LabelledInfo';
import CheckboxFilter, {
  compileCheckboxFilter,
} from '../molecules/ListFilter/CheckboxFilter';
import DropdownFilter from '../molecules/ListFilter/DropdownFilter';
import TextSearchFilter from '../molecules/ListFilter/TextSearchFilter';
import Loading from '../molecules/Loading';
import ErrorPage from '../pages/ErrorPage';
import {useToggleState} from '../utils/hooks';
import AmpTable, {defaultTrProps, Tr} from './Table';

const DateCell = ({row: {date}}) => (
  <Text>{date && moment.utc(date).format('YYYY-MM-DD')}</Text>
);

DateCell.propTypes = {
  row: PropTypes.object,
};

const kindLabel = {
  WORKORDER: 'Workorder',
  REQUEST: 'Request',
  DPR: 'DPR',
};

const KindCell = ({row: {kind}}) => (
  <Text style={{fontWeight: 'bold'}}>{kindLabel[kind] || ''}</Text>
);

KindCell.propTypes = {
  row: PropTypes.object,
};

const columns = [
  {
    Header: 'DATE',
    accessor: 'date',
    Cell: DateCell,
    width: 130,
    defaultSortDesc: true,
  },
  {
    Header: 'KIND',
    accessor: 'kind',
    Cell: KindCell,
    width: 120,
  },
  {
    Header: 'Stage',
    accessor: 'stage',
    width: 120,
  },
  {
    Header: 'TYPE/CODE',
    accessor: 'type',
    width: 120,
  },
  {
    Header: 'LEVEL',
    accessor: 'level',
    width: 90,
  },
  {
    Header: 'DESCRIPTION',
    accessor: 'description',
    width: 300,
  },
  {
    Header: 'Number',
    accessor: 'number',
    width: 170,
  },
];

const dataType = PropTypes.shape({
  date: PropTypes.string,
  kind: PropTypes.oneOf(['WORKORDER', 'REQUEST', 'DPR']),
  description: PropTypes.string,
  type: PropTypes.string,
  level: PropTypes.string,
  number: PropTypes.string,
  noteDescription: PropTypes.string,
  cause: PropTypes.string,
  remedy: PropTypes.string,
  additionalInfo: PropTypes.string,
  stage: PropTypes.string,
});

const ExpandedContent = ({data}) => (
  <>
    <LabelledInfo title="Description" subtitle={data.noteDescription || '-'} />
    <LabelledInfo
      title="Cause"
      subtitle={data.cause || '-'}
      style={{marginTop: '24px'}}
    />
    <LabelledInfo
      title="Solution"
      subtitle={data.remedy || '-'}
      style={{marginTop: '24px'}}
    />
    <LabelledInfo
      title="Additional information"
      subtitle={data.additionalInfo || '-'}
      style={{marginTop: '24px'}}
    />
  </>
);

ExpandedContent.propTypes = {
  data: dataType,
};

const filters = [
  {
    label: kindLabel.WORKORDER,
    fn: ({kind}) => kind === 'WORKORDER',
    checked: true,
  },
  {label: kindLabel.REQUEST, fn: ({kind}) => kind === 'REQUEST', checked: true},
  {label: kindLabel.DPR, fn: ({kind}) => kind === 'DPR', checked: true},
];

const Row = props =>
  props.isRow ? <ExpandableTableRow {...props} /> : <Tr {...props} />;

Row.propTypes = {
  isRow: PropTypes.bool,
};

const pickProps = compose(filter(Boolean), Object.values, pick);

const textSearchFn = value => item => {
  if (!value) {
    return true;
  }
  const keys = [
    'additionalInfo',
    'cause',
    'date',
    'description',
    'kind',
    'level',
    'noteDescription',
    'number',
    'remedy',
    'stage',
    'type',
  ];
  const textItems = pickProps(keys, item).map(toLower);
  const searchTerms = value ? toLower(value.trim()).split(/\s+/) : '';
  return searchTerms.every(term =>
    textItems.some(textItem => textItem.includes(term)),
  );
};

const stageOption = (stage, open = false) => ({
  label: stage,
  fn: x => x && x.stage && x.stage.toLowerCase() === stage.toLowerCase(),
  open,
});

const stageOptions = [
  stageOption('In preparation', true),
  stageOption('New', true),
  stageOption('Ready to start', true),
  stageOption('Work started', true),
  stageOption('Completed'),
  stageOption('On hold'),
  stageOption('Cancelled'),
  stageOption('Rejected'),
];

const MAINTENANCE_FRAGMENT = gql`
  fragment MaintenanceRelayFields on MaintenanceConnection {
    edges {
      node {
        date
        kind
        description
        type
        level
        number
        noteDescription
        cause
        remedy
        additionalInfo
        stage
      }
    }
  }
`;

const GET_SYSTEM_MAINTENANCE_HISTORY = gql`
  query System($id: ID!) {
    system(id: $id) {
      id
      maintenanceHistory(first: 1000) {
        ...MaintenanceRelayFields
      }
    }
  }
  ${MAINTENANCE_FRAGMENT}
`;

const GET_MAINTENANCE_HISTORY = gql`
  query Maintenances($stage: [String!]) {
    maintenances(first: 20000, stage: $stage) {
      ...MaintenanceRelayFields
    }
  }
  ${MAINTENANCE_FRAGMENT}
`;

const compileFilters = memoizeWith(
  (kind, stage, text) =>
    [...kind.map(x => x.checked), ...stage, text].join(','),
  (kindFilters, stageFilters, text) => {
    const kindFilter = compileCheckboxFilter(kindFilters);
    const stageFilter = anyPass(
      stageOptions
      .filter(({label}) => stageFilters.includes(label))
      .map(x => x.fn),
    );
    const textFilter = textSearchFn(text);

    return allPass([kindFilter, stageFilter, textFilter]);
  },
);

const MaintenanceHistoryFilters = ({
  onFilterFnChange,
  onServerFilterChange,
}) => {
  const [kindFilters, toggleKindFilter] = useToggleState(filters);
  const [stageFilters, setStageFilter] = useState(
    stageOptions.filter(x => x.open).map(x => x.label),
  );
  const [textSearch, setText] = useState('');

  const filterFn = compileFilters(kindFilters, stageFilters, textSearch);

  useEffect(() => {
    onFilterFnChange && onFilterFnChange(filterFn);
  }, [filterFn, onFilterFnChange]);

  const stageFilterHash = JSON.stringify(stageFilters);

  useEffect(() => {
    onServerFilterChange &&
      onServerFilterChange({
        kinds: kindFilters,
        stage: stageFilters,
        text: textSearch,
      });
  }, [
    kindFilters,
    stageFilters,
    stageFilterHash,
    textSearch,
    onServerFilterChange,
  ]);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      }}
    >
      <CheckboxFilter
        filterName="kind"
        label="Filter on kind:"
        filters={kindFilters}
        onChange={toggleKindFilter}
      />
      <div style={{display: 'flex', alignItems: 'center'}}>
        <DropdownFilter
          onFilterChange={setStageFilter}
          value={stageFilters}
          filterName="stage"
          options={stageOptions}
          style={{width: '150px'}}
          id="gtm-stage-maintenance"
        />
        <TextSearchFilter
          value={textSearch}
          onChange={setText}
          debounce={500}
          label="Search:"
          placeholder="Quickly search in description, cause, solution, etc."
          style={{marginLeft: '16px'}}
          inputProps={{id: 'gtm-search-maintenance'}}
        />
      </div>
    </div>
  );
};

MaintenanceHistoryFilters.propTypes = {
  onFilterFnChange: PropTypes.func,
  onServerFilterChange: PropTypes.func,
};

const MaintenanceHistoryContent = memo(
  ({data, loading, onServerFilterChange}) => {
    const [filterFn, setFilterFn] = useState(() => T);

    const filteredData = useMemo(() => data.filter(filterFn), [data, filterFn]);

    return (
      <>
        <MaintenanceHistoryFilters
          onServerFilterChange={onServerFilterChange}
          onFilterFnChange={fn => setFilterFn(() => fn)}
        />
        {loading ? (
          <Loading message="Fetching maintenance data..." />
        ) : (
          <AmpTable
            showPagination
            defaultPageSize={20}
            data={filteredData}
            style={{flex: 1, height: '100%'}}
            columns={columns}
            getTrProps={(_, row) => ({
              ...defaultTrProps,
              expandedContent: <ExpandedContent data={row.original} />,
              noRows: columns.length,
            })}
            TrComponent={Row}
            defaultSorted={[{id: 'date', desc: true}]}
          />
        )}
      </>
    );
  },
);

MaintenanceHistoryContent.propTypes = {
  data: PropTypes.array.isRequired,
  onServerFilterChange: PropTypes.func,
  loading: PropTypes.bool,
};

const SystemMaintenanceHistoryComponent = ({match}) => {
  const {
    params: {id},
  } = match;

  return (
    <Query query={GET_SYSTEM_MAINTENANCE_HISTORY} variables={{id}}>
      {({loading, error, data}) => {
        if (error) {
          return <ErrorPage error={error} />;
        }
        const displayData = data
          ? data.system.maintenanceHistory.edges.map(edge => edge.node)
          : [];
        return (
          <MaintenanceHistoryContent data={displayData} loading={loading} />
        );
      }}
    </Query>
  );
};

SystemMaintenanceHistoryComponent.propTypes = {
  match: PropTypes.object,
};

export const SystemMaintenanceHistory = withErrorBoundary(
  withRouter(SystemMaintenanceHistoryComponent),
);

const MaintenanceHistoryComponent = () => {
  const [filterVars, setFilterVars] = useState({});
  const [getMaintenances, {loading, error, data}] = useLazyQuery(
    GET_MAINTENANCE_HISTORY,
  );

  const filterVarsHash = JSON.stringify(filterVars);

  useEffect(() => {
    !isEmpty(filterVars) && getMaintenances({variables: filterVars});
  }, [filterVarsHash, getMaintenances, filterVars]);

  const onFilterChange = useCallback(
    // Only stage filter is handled server-side
    ({stage}) =>
      setFilterVars({
        stage,
      }),
    [setFilterVars],
  );

  const displayData = data
    ? data.maintenances.edges.map(edge => edge.node)
    : [];

  if (error) {
    return <ErrorPage error={error} />;
  }
  return (
    <MaintenanceHistoryContent
      data={displayData}
      loading={loading}
      onServerFilterChange={onFilterChange}
    />
  );
};

export const MaintenanceHistory = withErrorBoundary(
  withRouter(MaintenanceHistoryComponent),
);
